import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { AuthDataService } from '@nomoh-monorepo/data-access-auth';
import {
  APP_API_NAME,
  EnvironmentService,
} from '@nomoh-monorepo/shared-data-access-environment';
import { IAceitarTermo, ITermo, ITermoPendente } from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { BottomSheetComponent } from '../bottom-sheet/bottom-sheet.component';
import { DeviceService } from '../device/device.service';

@Component({
  selector: 'lib-termo-aceite-modal',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    ProgressSpinnerModule,
    ScrollPanelModule,
    BottomSheetComponent,
  ],
  templateUrl: './termo-aceite-modal.component.html',
  styleUrls: ['./termo-aceite-modal.component.scss'],
})
export class TermoAceiteModalComponent {
  private authDataService = inject(AuthDataService);
  private environment = inject(EnvironmentService);
  private appApiName = inject(APP_API_NAME);
  private http = inject(HttpClient);
  public deviceService = inject(DeviceService);

  private get baseUrl() {
    return `${this.environment.getApiUrl(this.appApiName)}/termo`;
  }

  private _termoAtual = signal<ITermo | null>(null);
  private _loading = signal(false);
  private _processando = signal(false);
  private _recusado = signal(false);

  $termoAtual = this._termoAtual.asReadonly();
  $loading = this._loading.asReadonly();
  $processando = this._processando.asReadonly();
  $recusado = this._recusado.asReadonly();

  $termosPendentes = this.authDataService.$termosPendentes;

  $visible = computed(
    () => this.$termosPendentes().length > 0 || this._recusado()
  );

  $progresso = computed(() => this.$termosPendentes().length);

  constructor() {
    // Reage automaticamente quando novos termos chegam após o login
    effect(() => {
      const pendentes = this.$termosPendentes();
      if (pendentes.length > 0) {
        this._carregarTermoAtual(pendentes[0]);
      } else if (!this._recusado()) {
        this._termoAtual.set(null);
      }
    });
  }

  private async _carregarTermoAtual(pendente: ITermoPendente): Promise<void> {
    this._loading.set(true);
    try {
      // sistemaId = UUID do sistema (vem do login response via $user().sistemaId)
      const sistemaId = this.authDataService.$user()?.sistemaId ?? '';
      const vigentes = await firstValueFrom(
        this.http.get<ITermo[]>(`${this.baseUrl}/vigente?sistema=${sistemaId}`)
      );
      const termo = vigentes.find((t) => t.id === pendente.termoId) ?? null;
      this._termoAtual.set(termo);
    } catch (error) {
      console.error('Erro ao carregar conteúdo do termo:', error);
    } finally {
      this._loading.set(false);
    }
  }

  async aceitar(): Promise<void> {
    const pendentes = this.$termosPendentes();
    if (!pendentes.length) return;

    this._processando.set(true);
    try {
      const dto: IAceitarTermo = { termoId: pendentes[0].termoId, aceito: true };
      await firstValueFrom(this.http.post<void>(`${this.baseUrl}/aceitar`, dto));
      this.authDataService.removeTermoAceito(pendentes[0].termoId);
      // O effect acima carrega o próximo automaticamente via signal
    } catch (error) {
      console.error('Erro ao aceitar termo:', error);
    } finally {
      this._processando.set(false);
    }
  }

  async recusar(): Promise<void> {
    const pendentes = this.$termosPendentes();
    if (!pendentes.length) return;

    this._processando.set(true);
    try {
      const dto: IAceitarTermo = { termoId: pendentes[0].termoId, aceito: false };
      await firstValueFrom(this.http.post<void>(`${this.baseUrl}/aceitar`, dto));
      this.authDataService.setTermosPendentes([]);
      this._recusado.set(true);
    } catch (error) {
      console.error('Erro ao recusar termo:', error);
    } finally {
      this._processando.set(false);
    }
  }

  async sair(): Promise<void> {
    this._recusado.set(false);
    await this.authDataService.logout();
  }
}
